import { type ReactElement, type ReactNode, useState } from 'react';
import classNames from 'classnames';

import { HotelOfferListItem } from './components/hotelOfferListItem/HotelOfferListItem';
import { type HotelOffer, KayakHotelProvider, type RoomWithPeople } from '@/interfaces';
import { listOfSkeletons } from './components/skeletonOffer/SkeletonOffer';
import { NoOffersWarning } from '../noOffersWarning/NoOffersWarning';
import { LoadingItem } from '@/components/loadingItem/LoadingItem';
import { scrollToElementById } from '@/utils/scroll';
import { trackLoadMorePrices } from '@/utils/tracking/finnAnalytics';

export interface Props {
    id: string;
    offers: readonly HotelOffer[];
    rooms: readonly RoomWithPeople[];
    nightsSearched: number;
    checkInDate?: Date | number;
    checkOutDate?: Date | number;
    isPending: boolean;
    filtersSelected: boolean;
    title?: string;
    extraInfo?: ReactElement;
    className?: string;
    hideWhenEmpty?: boolean;
    hideOnMobile?: boolean;
    hideOnDesktop?: boolean;
    defaultOffersShown?: number;
    isPrimary?: boolean;
}

export const HotelOfferList = ({
    id,
    offers,
    rooms,
    nightsSearched,
    checkInDate,
    checkOutDate,
    isPending,
    filtersSelected,
    title,
    extraInfo,
    className = '',
    hideWhenEmpty = false,
    hideOnMobile = false,
    hideOnDesktop = false,
    defaultOffersShown = 2,
    isPrimary = false,
}: Props) => {
    const [expanded, setExpanded] = useState(false);
    const isLoading = isPending;

    const onButtonClick = (): void => {
        if (expanded) {
            scrollToElementById(id);
        } else {
            trackLoadMorePrices();
        }
        setExpanded(!expanded);
    };

    if (hideWhenEmpty && offers.length === 0 && !isPending) {
        return null;
    }

    const isDirect = (offer?: HotelOffer): boolean => offer?.provider?.code === KayakHotelProvider.REVATO || offer?.isDirect;
    const visibleOffers = offers.slice(0, expanded ? offers.length : defaultOffersShown);
    const mappedOffers = visibleOffers.map(
        (offer: HotelOffer, idx: number): ReactNode => (
            <HotelOfferListItem
                key={`offer-list-item-${offer.id}`}
                offer={offer}
                rooms={rooms}
                nights={nightsSearched}
                useDirectBanner={hideOnDesktop}
                nextIsDirect={isDirect(visibleOffers[idx + 1])}
                previousIsDirect={isDirect(visibleOffers[idx - 1])}
                isPrimary={isPrimary}
            />
        ),
    );
    return (
        <div id={id} className={classNames('u-mt32', { 'u-hide-lt768': hideOnMobile, 'u-hide-gt768': hideOnDesktop })} data-testid={id}>
            {title && (
                <div className="flex">
                    <h3 className="u-t3 u-strong u-no-break">{title}</h3>
                    {extraInfo}
                </div>
            )}
            <div className={`panel ${className} u-mh0`}>
                <div className="u-position-relative ads ads--list ads--cards">
                    {isLoading ? <LoadingItem classNames="u-pa8 u-mv16" /> : mappedOffers}
                    {isLoading && mappedOffers.concat(listOfSkeletons(2)).slice(0, expanded ? offers.length : defaultOffersShown)}
                </div>
                {offers.length > defaultOffersShown && (
                    <div className="u-text-center u-pv16">
                        <button
                            type="button"
                            className="button button--link button--link--strong"
                            data-testid="show-more-prices"
                            onClick={onButtonClick}>
                            {expanded ? 'Vis færre priser' : `Vis flere priser (${offers.length - defaultOffersShown})`}
                        </button>
                    </div>
                )}
                {!isLoading && mappedOffers.length === 0 && (
                    <NoOffersWarning checkInDate={checkInDate} checkOutDate={checkOutDate} filtersSelected={filtersSelected} />
                )}
            </div>
        </div>
    );
};
